import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  from {
    background-position: 100% 0;
  }
  to {
    background-position: -100% 0;
  }
`;

/**
 * Both ends of the sweep are the placeholder's own colour, so the band has no seam;
 * the crest is that colour pulled towards the card it sits on, which lightens it in
 * the light theme and darkens it in the dark one without leaving the palette.
 */
const shimmerBand = `linear-gradient(
    90deg,
    var(--clr-border-grey-100) 0%,
    color-mix(in srgb, var(--clr-border-grey-100) 55%, var(--clr-container-bg) 45%) 50%,
    var(--clr-border-grey-100) 100%
  )`;

interface SkeletonLineProps {
  $width?: string;
  $height?: string;
  $radius?: string;
  'data-testid'?: string;
}

/**
 * Built from styled-components rather than antd's Skeleton so the placeholders stay
 * independent of the antd module — many suites mock `antd` partially, and a skeleton
 * appearing anywhere in the tree must not depend on what those mocks happen to export.
 */
export const SkeletonLine = styled.span.attrs<SkeletonLineProps>((props) => ({
  'data-testid': props['data-testid'] ?? 'skeleton-line',
}))`
  display: block;
  flex-shrink: 0;
  width: ${({ $width }) => $width ?? '100%'};
  /* Keeps the design's fixed pixel widths inside a narrow task pane and inside the
     two-column rows, where the available width is well under the design's canvas. */
  max-width: 100%;
  height: ${({ $height }) => $height ?? '13px'};
  border-radius: ${({ $radius }) => $radius ?? '4px'};
  background-color: var(--clr-border-grey-100);
  background-image: ${shimmerBand};
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    background-image: none;
    animation: none;
  }
`;

/**
 * Reserves the exact line-height of the text it stands in for, so a skeleton built
 * from these is the same height as the real content and nothing shifts on swap.
 */
export const SkeletonTextLine = styled.div<{ $lineheight: string }>`
  height: ${({ $lineheight }) => $lineheight};
  display: flex;
  align-items: center;
`;

export default SkeletonLine;
